import { AIRTABLE_TABLES, fetchAllAirtableRecords } from './airtable';
import { getFirebaseAuth } from './firebase';

const FINIQUITOS_API_URL = process.env.REACT_APP_FINIQUITOS_API_URL || '/finiquitos-docx';
const FINIQUITOS_RENDER_FINIQUITO_PATH =
  process.env.REACT_APP_FINIQUITOS_RENDER_FINIQUITO_PATH || '/render/finiquito';
const FINIQUITOS_RENDER_BUNDLE_PATH =
  process.env.REACT_APP_FINIQUITOS_RENDER_BUNDLE_PATH || '/render/bundle';
const FINIQUITOS_HEALTH_PATH = process.env.REACT_APP_FINIQUITOS_HEALTH_PATH || '/health';
const FINIQUITOS_EMPLOYERS_TABLE_ID =
  process.env.REACT_APP_AIRTABLE_FINIQUITOS_EMPLOYERS_TABLE_ID ||
  AIRTABLE_TABLES.clientes ||
  process.env.REACT_APP_AIRTABLE_CLIENTES_TABLE_ID ||
  'tblC5Jn5qbpiIEh2V';
const FINIQUITOS_API_CONFIG_ERROR_MESSAGE =
  'Configura REACT_APP_FINIQUITOS_API_URL para conectar el backend DOCX de finiquitos.';

function normalizeText(value) {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    const first = value.find(item => normalizeText(item));
    return normalizeText(first);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return String(value).trim();
}

function cleanArray(values = []) {
  return Array.isArray(values)
    ? values
        .map(value => normalizeText(value))
        .filter(Boolean)
    : [];
}

function pickFirstText(fields = {}, keys = []) {
  for (const key of keys) {
    const candidate = normalizeText(fields?.[key]);
    if (candidate) {
      return candidate;
    }
  }
  return '';
}

function normalizeActiveStatus(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) return false;
  return ['activo', 'active', 'vigente', 'true', '1', 'si', 'sí', 'enabled'].includes(normalized);
}

export function isFiniquitoEmployerActive(fields = {}) {
  const statusValue = fields.ESTATUS ?? fields.Estatus ?? fields.estatus ?? fields.Estado ?? fields.estado;
  return normalizeActiveStatus(statusValue);
}

function buildEmployerLabel(fields = {}, recordId = '') {
  const company =
    pickFirstText(fields, [
      'Empresa',
      'Razón Social',
      'Razon Social',
      'Nombre',
      'Alias',
      'NOMBRE COMERCIAL',
      'Nombre comercial',
      'Compañía',
      'Compania'
    ]) || recordId;
  const rfc = pickFirstText(fields, ['RFC', 'rfc', 'RFC Patronal']);
  const patronal = pickFirstText(fields, [
    'REG PATRONAL',
    'Registro Patronal',
    'Registro patronal',
    'registro patronal',
    'Registro',
    'RP'
  ]);
  const code = pickFirstText(fields, ['ID Cliente', 'Airtable ID', 'ID']);
  const details = [rfc, patronal, code].filter(Boolean).join(' · ');

  return {
    label: details ? `${company} · ${details}` : company,
    company,
    rfc,
    patronal,
    registroPatronal: patronal,
    code
  };
}

function normalizeProcessStatus(value) {
  const comparable = normalizeText(value).toLowerCase();
  if (!comparable) return '';
  if (['generated', 'generado', 'ready', 'listo', 'ok', 'success', 'completado', 'complete'].includes(comparable)) {
    return 'generated';
  }
  if (['generating', 'generando', 'processing', 'running', 'in_progress', 'en curso'].includes(comparable)) {
    return 'generating';
  }
  if (['pending', 'pendiente', 'queued', 'en cola', 'waiting', 'esperando'].includes(comparable)) {
    return 'pending';
  }
  if (['error', 'errored', 'failed', 'rechazado', 'rechazada', 'invalid'].includes(comparable)) {
    return 'error';
  }
  return comparable;
}

function normalizeHealthStatus(value) {
  const comparable = normalizeText(value).toLowerCase();
  if (!comparable) return '';
  if (['ready', 'healthy', 'healthy_ok', 'ok', 'up', 'live', 'available', 'true', '1'].includes(comparable)) {
    return 'ready';
  }
  if (['checking', 'pending', 'pendiente', 'verificando'].includes(comparable)) {
    return 'checking';
  }
  if (['unhealthy', 'down', 'degraded', 'error', 'failed', 'fail'].includes(comparable)) {
    return 'unhealthy';
  }
  if (['unavailable', 'offline', 'timeout', 'missing'].includes(comparable)) {
    return 'unavailable';
  }
  return comparable;
}

function normalizeDownloadUrl(value, endpoint = FINIQUITOS_API_URL) {
  const text = normalizeText(value);
  if (!text) return '';
  if (/^https?:\/\//i.test(text) || /^data:/i.test(text) || /^blob:/i.test(text)) {
    return text;
  }
  if (!endpoint) return text;
  return resolveFiniquitosEndpoint(endpoint, text);
}

function normalizeStorageLocation(value) {
  if (!value || typeof value !== 'object') {
    const text = normalizeText(value);
    return text ? { id: text, path: '', url: '' } : null;
  }

  const id = normalizeText(value.id || value.storageId || value.storage_id || value.key || value.name);
  const path = normalizeText(value.path || value.storagePath || value.storage_path || value.location || value.folder);
  const url = normalizeDownloadUrl(value.url || value.downloadUrl || value.download_url || value.href);
  const provider = normalizeText(value.provider || value.backend || value.system);

  if (!id && !path && !url && !provider) {
    return null;
  }

  return {
    id,
    path,
    url,
    provider
  };
}

function deriveDocumentDownloadUrl(record = {}) {
  const filePath = normalizeText(
    record.fileId ||
      record.file_id ||
      record.relativePath ||
      record.relative_path ||
      record.storagePath ||
      record.storage_path ||
      ''
  ).replace(/\\/g, '/');
  const match = filePath.match(/(?:^|\/)renders\/([^/]+)\/([^/]+)\/([^/]+\.docx)$/i);
  if (!match) return '';
  const [, requestId, kind, fileName] = match;
  return `/documents/${encodeURIComponent(requestId)}/${encodeURIComponent(kind)}/${encodeURIComponent(fileName)}`;
}

function normalizeDocumentRecord(record = {}, index = 0, endpoint = FINIQUITOS_API_URL) {
  const type = normalizeText(record.type || record.documentType || record.document_type || record.kind).toLowerCase();
  const status = normalizeProcessStatus(record.status || record.estado || record.state);
  const label = normalizeText(record.label || record.name || record.title) || (type === 'renuncia' ? 'Renuncia' : 'Finiquito');
  const fileName = normalizeText(record.fileName || record.file_name || record.filename || record.filenameDisplay || record.logicalFileName);
  const storage = normalizeStorageLocation(
    record.storage ||
      record.storageInfo ||
      record.storage_info ||
      record.location ||
      record.fileId ||
      record.file_id ||
      record.relativePath ||
      record.relative_path
  );
  const hashSha256 =
    normalizeText(record.hashSha256 || record.hash_sha256 || record.sha256 || record.digest || record.fileHash) || '';
  const downloadUrl = normalizeDownloadUrl(
    record.downloadUrl || record.download_url || record.url || record.href || storage?.url || deriveDocumentDownloadUrl(record),
    endpoint
  );
  const warnings = cleanArray(record.warnings || record.warning || []);

  return {
    id: normalizeText(record.id || record.documentId || record.document_id || `${type || 'document'}-${index}`),
    type: type || `documento_${index + 1}`,
    label,
    status: status || 'pending',
    fileName,
    hashSha256,
    downloadUrl,
    storage,
    warnings
  };
}

function extractDocumentRecords(data = {}) {
  if (Array.isArray(data.documents)) return data.documents;
  if (Array.isArray(data.files)) return data.files;
  if (data.bundle && typeof data.bundle === 'object') {
    if (Array.isArray(data.bundle.documents)) return data.bundle.documents;
    return ['finiquito', 'renuncia']
      .map(key => data.bundle[key])
      .filter(Boolean)
      .map((value, index) => ({
        ...(typeof value === 'object' ? value : {}),
        type: ['finiquito', 'renuncia'][index]
      }));
  }
  if (data.document && typeof data.document === 'object') {
    return [data.document];
  }
  return [];
}

function deriveOverallStatus(data = {}, documents = []) {
  const explicitStatus = normalizeProcessStatus(data.status || data.state || data.estado || data.documentStatus);
  if (['generated', 'generating', 'pending', 'error'].includes(explicitStatus)) {
    return explicitStatus;
  }

  const statuses = documents.map(document => normalizeProcessStatus(document.status)).filter(Boolean);
  if (statuses.includes('error')) return 'error';
  if (statuses.includes('generating')) return 'generating';
  if (statuses.includes('pending')) return 'pending';
  if (statuses.length && statuses.every(status => status === 'generated')) return 'generated';
  return 'pending';
}

function normalizeHash(value) {
  return normalizeText(value);
}

function normalizeFiniquitosResponse(data = {}, endpoint = FINIQUITOS_API_URL) {
  const documents = extractDocumentRecords(data).map((record, index) => normalizeDocumentRecord(record, index, endpoint));
  const status = deriveOverallStatus(data, documents);
  const storage = normalizeStorageLocation(data.storage || data.storageInfo || data.storage_info);
  const warnings = cleanArray(data.warnings || data.warning || data.alerts || []);
  const message = normalizeText(data.message || data.errorMessage || data.error_message || data.reason);
  const code = normalizeText(data.code || data.errorCode || data.error_code);
  const calculationRunId = normalizeText(
    data.calculation_run_id || data.calculationRunId || data.runId || data.run_id || data.id
  );
  const templateVersion = normalizeText(data.template_version || data.templateVersion || data.template || data.version);
  const hashSha256 = normalizeHash(data.hashSha256 || data.hash_sha256 || data.sha256 || data.digest || '');

  return {
    status,
    message,
    code,
    calculationRunId,
    templateVersion,
    hashSha256,
    storage,
    documents,
    warnings,
    snapshot:
      data.documentSnapshot ||
      data.document_snapshot ||
      data.snapshot ||
      data.approved_snapshot ||
      data.approvedSnapshot ||
      null,
    rawStatus: normalizeText(data.status || data.state || data.estado || data.documentStatus)
  };
}

function buildApiError(message, { kind = 'business', code = '', status = 0, details = [], response = null } = {}) {
  const error = new Error(message);
  error.name = 'FiniquitosApiError';
  error.kind = kind;
  error.code = code;
  error.status = status;
  error.details = Array.isArray(details) ? details : [];
  if (response) {
    error.response = response;
  }
  return error;
}

function classifyHttpErrorKind(status) {
  if (status === 401 || status === 403) {
    return 'auth';
  }
  if (status >= 500) {
    return 'technical';
  }
  return 'business';
}

function extractErrorMessage(data, fallbackMessage) {
  const candidates = [
    data?.message,
    data?.errorMessage,
    data?.error?.message,
    data?.detail,
    data?.reason,
    data?.raw
  ];
  const message = candidates.map(normalizeText).find(Boolean);
  return message || fallbackMessage;
}

function isResponseBusinessFailure(data = {}) {
  const status = normalizeProcessStatus(data.status || data.state || data.estado || data.documentStatus);
  if (status === 'error') return true;
  if (data.ok === false || data.success === false) return true;
  if (data.error || data.errorCode || data.error_code) return true;
  return false;
}

async function parseJsonResponse(response) {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch (error) {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return { raw: text, parseError: error?.message || 'invalid_json' };
    }
    return { raw: text };
  }
}

async function parseTextResponse(response) {
  const text = await response.text();
  try {
    return { json: text ? JSON.parse(text) : null, raw: text };
  } catch (error) {
    return { json: null, raw: text, parseError: error?.message || 'invalid_json' };
  }
}

function resolveFiniquitosEndpoint(endpoint = FINIQUITOS_API_URL, path = '') {
  const base = String(endpoint || '').trim();
  if (!base) return '';
  const normalizedBase = base.replace(/\/+$/, '');
  const normalizedPath = String(path || '').trim().replace(/^\/+/, '');
  return normalizedPath ? `${normalizedBase}/${normalizedPath}` : normalizedBase;
}

async function resolveFiniquitosAuthToken() {
  try {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) {
      throw buildApiError('Inicia sesión de nuevo para generar o descargar los DOCX.', {
        kind: 'auth',
        code: 'AUTH_REQUIRED',
        status: 401
      });
    }
    return await user.getIdToken();
  } catch (error) {
    if (error?.kind === 'auth') {
      throw error;
    }
    throw buildApiError('No fue posible obtener la sesión autenticada para finiquitos.', {
      kind: 'auth',
      code: 'AUTH_UNAVAILABLE',
      details: [error?.message || 'firebase_auth_unavailable']
    });
  }
}

function buildRequestHeaders(token) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function submitFiniquitosRequest({ payload, endpoint, path, requireAuth = true } = {}) {
  const resolvedEndpoint = resolveFiniquitosEndpoint(endpoint, path);
  if (!resolvedEndpoint) {
    throw buildApiError('Configura REACT_APP_FINIQUITOS_API_URL para enviar el cálculo y generar DOCX desde el backend.', {
      kind: 'config',
      code: 'FINIQUITOS_API_URL_MISSING'
    });
  }

  const token = requireAuth ? await resolveFiniquitosAuthToken() : '';
  const response = await fetch(resolvedEndpoint, {
    method: 'POST',
    headers: buildRequestHeaders(token),
    body: JSON.stringify(payload)
  });
  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw buildApiError(
      extractErrorMessage(data, `HTTP ${response.status}`),
      {
        kind: classifyHttpErrorKind(response.status),
        code: normalizeText(data?.code || data?.errorCode || data?.error_code || ''),
        status: response.status,
        details: cleanArray(data?.details || data?.errors || data?.warnings),
        response: undefined
      }
    );
  }

  if (isResponseBusinessFailure(data)) {
    throw buildApiError(extractErrorMessage(data, 'La generación documental fue rechazada por el backend.'), {
      kind: 'business',
      code: normalizeText(data?.code || data?.errorCode || data?.error_code || 'FINIQUITOS_BUSINESS_ERROR'),
      status: response.status,
      details: cleanArray(data?.details || data?.errors || data?.warnings)
    });
  }

  return normalizeFiniquitosResponse(data, endpoint);
}

export function getFiniquitosApiConfigError() {
  if (!FINIQUITOS_API_URL) {
    return FINIQUITOS_API_CONFIG_ERROR_MESSAGE;
  }
  return '';
}

export function isFiniquitosApiConfigured() {
  return !getFiniquitosApiConfigError();
}

export function getFiniquitosEmployersTableId() {
  return FINIQUITOS_EMPLOYERS_TABLE_ID;
}

export async function fetchFiniquitoEmployers({
  tableId = FINIQUITOS_EMPLOYERS_TABLE_ID,
  maxRecords = 250
} = {}) {
  if (!tableId) return [];

  const records = await fetchAllAirtableRecords({
    tableId,
    maxRecords,
    filterByFormula: '{ESTATUS}="ACTIVO"'
  });

  return records
    .map(record => {
      const fields = record.fields || {};
      if (!isFiniquitoEmployerActive(fields)) {
        return null;
      }
      const employer = buildEmployerLabel(fields, record.id);
      const searchValue = [
        employer.label,
        employer.company,
        employer.rfc,
        employer.patronal,
        employer.registroPatronal,
        employer.code,
        record.id,
        normalizeText(fields['Airtable ID']),
        normalizeText(fields.Contacto),
        normalizeText(fields.Correo),
        normalizeText(fields.Telefono),
        normalizeText(fields['NOMBRE COMERCIAL']),
        normalizeText(fields.Alias),
        normalizeText(fields['REG PATRONAL'])
      ]
        .filter(Boolean)
        .map(item => item.toLowerCase())
        .join(' ');

      return {
        id: record.id,
        ...employer,
        airtableId: normalizeText(fields['Airtable ID']) || record.id,
        searchValue,
        status: normalizeText(fields.ESTATUS || fields.Estatus || fields.estatus || ''),
        rawFields: fields
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.label.localeCompare(right.label, 'es-MX'));
}

export async function submitFiniquitoDocument({ payload, endpoint = FINIQUITOS_API_URL, requireAuth = true } = {}) {
  return submitFiniquitosRequest({
    payload,
    endpoint,
    path: FINIQUITOS_RENDER_FINIQUITO_PATH,
    requireAuth
  });
}

export async function submitFiniquitosBundle({ payload, endpoint = FINIQUITOS_API_URL, requireAuth = true } = {}) {
  return submitFiniquitosRequest({
    payload,
    endpoint,
    path: FINIQUITOS_RENDER_BUNDLE_PATH,
    requireAuth
  });
}

export async function submitFiniquitoCalculation({ payload, endpoint = FINIQUITOS_API_URL, requireAuth = true } = {}) {
  return submitFiniquitoDocument({
    payload,
    endpoint,
    requireAuth
  });
}

export async function checkFiniquitosBackendHealth({ endpoint = FINIQUITOS_API_URL } = {}) {
  const resolvedEndpoint = resolveFiniquitosEndpoint(endpoint, FINIQUITOS_HEALTH_PATH);
  if (!resolvedEndpoint) {
    return {
      status: 'unconfigured',
      ready: false,
      checked: true,
      message: FINIQUITOS_API_CONFIG_ERROR_MESSAGE,
      code: 'FINIQUITOS_API_URL_MISSING',
      details: [],
      raw: null
    };
  }

  try {
    const response = await fetch(resolvedEndpoint, {
      method: 'GET',
      headers: {
        Accept: 'application/json, text/plain, */*'
      }
    });
    const { json, raw } = await parseTextResponse(response);
    const data = json && typeof json === 'object' ? json : {};
    const status = normalizeHealthStatus(data.status || data.state || data.health || data.healthStatus);
    const rawStatus = normalizeText(raw).toLowerCase();
    const readyFlags = [
      data.ok,
      data.ready,
      data.healthy,
      status === 'ready',
      status === 'live',
      status === 'up',
      ['ok', 'healthy', 'ready', 'live', 'up'].includes(rawStatus)
    ];
    const ready = Boolean(readyFlags.find(flag => flag === true));
    const message = normalizeText(data.message || data.errorMessage || data.detail || data.reason || raw);
    const code = normalizeText(data.code || data.errorCode || data.error_code);

    if (!response.ok) {
      return {
        status: response.status >= 500 ? 'unavailable' : 'unhealthy',
        ready: false,
        checked: true,
        message:
          message ||
          `El health del backend DOCX respondió HTTP ${response.status}.`,
        code,
        details: cleanArray(data.details || data.errors || data.warnings),
        raw: data
      };
    }

    if (!ready) {
      return {
        status: 'unhealthy',
        ready: false,
        checked: true,
        message:
          message ||
          'El health del backend DOCX respondió, pero no marcó el servicio como listo.',
        code,
        details: cleanArray(data.details || data.errors || data.warnings),
        raw: data
      };
    }

    return {
      status: 'ready',
      ready: true,
      checked: true,
      message: message || 'Backend DOCX listo.',
      code,
      details: cleanArray(data.details || data.errors || data.warnings),
      raw: data
    };
  } catch (error) {
    return {
      status: 'unavailable',
      ready: false,
      checked: true,
      message: error?.message || 'No fue posible consultar el health del backend DOCX.',
      code: normalizeText(error?.code || error?.errorCode || 'FINIQUITOS_HEALTH_UNAVAILABLE'),
      details: cleanArray(error?.details || [error?.message || 'health_unavailable']),
      raw: null
    };
  }
}

export async function downloadFiniquitosDocument({
  downloadUrl,
  filename = '',
  endpoint = FINIQUITOS_API_URL,
  requireAuth = true
} = {}) {
  const rawDownloadUrl = normalizeText(downloadUrl);
  const isSpecialUrl = /^blob:/i.test(rawDownloadUrl) || /^data:/i.test(rawDownloadUrl);
  const isAbsoluteUrl = /^https?:\/\//i.test(rawDownloadUrl) || isSpecialUrl;

  if (!rawDownloadUrl) {
    throw buildApiError('El backend no devolvió una URL de descarga autenticada.', {
      kind: 'business',
      code: 'FINIQUITOS_DOWNLOAD_URL_MISSING'
    });
  }

  if (!isAbsoluteUrl && !endpoint) {
    throw buildApiError('Configura REACT_APP_FINIQUITOS_API_URL para resolver la descarga DOCX.', {
      kind: 'config',
      code: 'FINIQUITOS_API_URL_MISSING'
    });
  }

  const resolvedUrl = normalizeDownloadUrl(rawDownloadUrl, endpoint);
  if (!resolvedUrl) {
    throw buildApiError('El backend no devolvió una URL de descarga autenticada.', {
      kind: 'business',
      code: 'FINIQUITOS_DOWNLOAD_URL_MISSING'
    });
  }

  const shouldAttachAuth =
    requireAuth &&
    !isSpecialUrl &&
    (!/^https?:\/\//i.test(resolvedUrl) ||
      (() => {
        try {
          if (!endpoint) return false;
          return new URL(resolvedUrl).origin === new URL(resolveFiniquitosEndpoint(endpoint)).origin;
        } catch (error) {
          return false;
        }
      })());

  const token = shouldAttachAuth ? await resolveFiniquitosAuthToken() : '';
  const response = await fetch(resolvedUrl, {
    method: 'GET',
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
          Accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/octet-stream'
        }
      : {
          Accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/octet-stream'
        }
  });

  if (!response.ok) {
    const data = await parseJsonResponse(response);
    throw buildApiError(extractErrorMessage(data, `HTTP ${response.status}`), {
      kind: classifyHttpErrorKind(response.status),
      code: normalizeText(data?.code || data?.errorCode || data?.error_code || ''),
      status: response.status,
      details: cleanArray(data?.details || data?.errors || data?.warnings)
    });
  }

  return {
    blob: await response.blob(),
    filename: normalizeText(filename),
    contentType: response.headers.get('content-type') || 'application/octet-stream',
    url: resolvedUrl
  };
}
